import {
  animate,
  Animator,
  ImageView,
  ImageViewOpts,
  MultiplyFilter,
} from '@play-co/timestep-core/ui';
import { loader } from '@play-co/timestep-core';

export default class LazyImageView extends ImageView {
  private spinner: ImageView | undefined;
  private noSpinner: boolean;
  public spinnerColor: string | undefined;
  private spinnerAnimation: Animator | undefined;
  private image: string | undefined;
  constructor(
    opts: ImageViewOpts & {
      noSpinner?: boolean;
      spinnerColor?: string;
    },
  ) {
    super(opts);

    this.noSpinner = opts.noSpinner ?? false;
    this.spinnerColor = opts.spinnerColor;

    const image = opts.image;
    if (image) {
      this.updateImage(image as string);
    }
  }

  public async updateImage(image: string) {
    // Save actual image
    this.image = image;

    super.setImage(image);

    const imageWrapper = this.getImage();
    if (imageWrapper === null) {
      return;
    }

    const isImageLoaded = loader.cache[image];
    if (isImageLoaded) {
      // No need to display the spinner
      // Making sure it's invisible
      if (this.spinner != null) {
        this.spinner.hide();
        this.spinnerAnimation?.clear();
      }

      // Show image
      this.style.visible = true;

      return;
    }

    // Showing the spinner
    if (this.noSpinner === false && this.spinner == null) {
      const imageViewStyle = this.style;
      const imageViewWidth = imageViewStyle.width;
      const imageViewHeight = imageViewStyle.height;
      const spinnerDim = Math.min(
        0.8 * imageViewWidth,
        0.8 * imageViewHeight,
        92,
      );

      const superview = this.getSuperview();

      if (superview) {
        this.spinner = new ImageView({
          superview,
          image: 'assets/ui/shared/icons/loading_spinner.png',
          x: imageViewStyle.x + imageViewWidth / 2 + imageViewStyle.offsetX,
          y: imageViewStyle.y + imageViewHeight / 2 + imageViewStyle.offsetY,
          width: spinnerDim,
          height: spinnerDim,
          centerOnOrigin: true,
        });

        if (this.spinnerColor) {
          this.spinner.setFilter(new MultiplyFilter(this.spinnerColor));
        }

        // Cache animator
        this.spinnerAnimation = animate(this.spinner);
      }
    }

    const spin = 2 * Math.PI;
    const spinDuration = 1000;

    if (this.spinner != null) {
      this.spinner?.updateOpts({
        scale: 1,
        r: 0,
        opacity: 1,
        visible: true,
      });

      const loopSpinner = () => {
        this.spinnerAnimation
          ?.clear()
          .then({ r: 0 }, 0)
          .then({ r: spin }, spinDuration, animate.linear)
          .then(loopSpinner);
      };

      loopSpinner();
    }

    this.style.visible = false;

    await loader.loadAsset(image);

    // Other image in progress this one is not actual
    if (image !== this.image) return;

    this.style.visible = true;

    if (this.spinner && loader.cache[image]) {
      this.spinner.style.visible = false;
      this.spinnerAnimation?.clear();
    }
  }
}
